import { useState, useEffect } from "react";
import { Plus, Edit, Trash2, Eye, EyeOff, Save, X, Image, RefreshCw, Clock, Calendar } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SITE_URL = "https://flowing.lat";

const CATEGORIES = [
  "Tecnología",
  "Internet",
  "Tips",
  "Noticias",
  "Tutoriales",
  "Gaming",
  "Streaming"
];

export const PostsTab = ({ getAuthHeaders, showNotification }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPost, setEditingPost] = useState(null);
  const [showEditor, setShowEditor] = useState(false);

  const emptyPost = {
    title: "",
    slug: "",
    excerpt: "",
    content: "",
    image: "",
    category: "Tecnología",
    read_time: "5 min",
    published: true,
    scheduled_at: ""
  };

  const [postForm, setPostForm] = useState(emptyPost);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/blog/posts`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setPosts(data);
      }
    } catch (error) {
      console.error("Error fetching posts:", error);
    }
    setLoading(false);
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .trim();
  };

  const handleTitleChange = (e) => {
    const title = e.target.value;
    setPostForm({
      ...postForm,
      title,
      slug: generateSlug(title)
    });
  };

  const handleSavePost = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const url = editingPost
        ? `${API_URL}/api/admin/blog/posts/${editingPost.id}`
        : `${API_URL}/api/admin/blog/posts`;

      const response = await fetch(url, {
        method: editingPost ? "PUT" : "POST",
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(postForm),
      });

      if (response.ok) {
        showNotification(
          editingPost ? "Post actualizado correctamente" : "Post creado correctamente",
          "success"
        );
        setShowEditor(false);
        setEditingPost(null);
        setPostForm(emptyPost);
        fetchPosts();
      } else {
        const error = await response.json();
        showNotification(error.detail || "Error al guardar el post", "error");
      }
    } catch (error) {
      showNotification("Error de conexión", "error");
    }
    setLoading(false);
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm("¿Estás seguro de eliminar este post?")) return;

    try {
      const response = await fetch(`${API_URL}/api/admin/blog/posts/${postId}`, {
        method: "DELETE",
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        showNotification("Post eliminado correctamente", "success");
        fetchPosts();
      }
    } catch (error) {
      showNotification("Error al eliminar el post", "error");
    }
  };

  const handleEditPost = (post) => {
    setEditingPost(post);
    setPostForm({
      title: post.title,
      slug: post.slug,
      excerpt: post.excerpt,
      content: post.content,
      image: post.image,
      category: post.category,
      read_time: post.read_time,
      published: post.published,
      scheduled_at: post.scheduled_at || ""
    });
    setShowEditor(true);
  };

  const handleTogglePublish = async (post) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/blog/posts/${post.id}`, {
        method: "PUT",
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ published: !post.published }),
      });

      if (response.ok) {
        showNotification(
          post.published ? "Post despublicado" : "Post publicado",
          "success"
        );
        fetchPosts();
      }
    } catch (error) {
      showNotification("Error al cambiar estado", "error");
    }
  };

  if (showEditor) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-gray-900">
            {editingPost ? "Editar Post" : "Nuevo Post"}
          </h2>
          <button
            onClick={() => {
              setShowEditor(false);
              setEditingPost(null);
              setPostForm(emptyPost);
            }}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSavePost} className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Título</label>
              <input
                type="text"
                value={postForm.title}
                onChange={handleTitleChange}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Slug (URL)</label>
              <input
                type="text"
                value={postForm.slug}
                onChange={(e) => setPostForm({ ...postForm, slug: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Extracto</label>
            <textarea
              value={postForm.excerpt}
              onChange={(e) => setPostForm({ ...postForm, excerpt: e.target.value })}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
              rows={2}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Contenido</label>
            <textarea
              value={postForm.content}
              onChange={(e) => setPostForm({ ...postForm, content: e.target.value })}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 font-mono text-sm"
              rows={15}
              required
              placeholder="Usa Markdown: ## para títulos, **texto** para negrita, - para listas"
            />
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">URL de Imagen</label>
              <input
                type="url"
                value={postForm.image}
                onChange={(e) => setPostForm({ ...postForm, image: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                placeholder="https://..."
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Categoría</label>
              <select
                value={postForm.category}
                onChange={(e) => setPostForm({ ...postForm, category: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
              >
                {CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Tiempo de lectura</label>
              <input
                type="text"
                value={postForm.read_time}
                onChange={(e) => setPostForm({ ...postForm, read_time: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                placeholder="5 min"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Clock className="w-4 h-4 inline mr-1" />
                Programar publicación (opcional)
              </label>
              <input
                type="datetime-local"
                value={postForm.scheduled_at}
                onChange={(e) => setPostForm({ ...postForm, scheduled_at: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <div className="flex items-center">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={postForm.published}
                  onChange={(e) => setPostForm({ ...postForm, published: e.target.checked })}
                  className="w-4 h-4 text-orange-500 rounded focus:ring-orange-500"
                />
                <span className="text-sm font-medium text-gray-700">Publicado</span>
              </label>
            </div>
          </div>

          {postForm.image && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Vista previa</label>
              <img
                src={postForm.image}
                alt="Preview"
                className="w-full max-h-48 object-cover rounded-lg"
                onError={(e) => e.target.style.display = 'none'}
              />
            </div>
          )}

          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={() => {
                setShowEditor(false);
                setEditingPost(null);
                setPostForm(emptyPost);
              }}
              className="px-6 py-2 border rounded-lg hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center gap-2 px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {loading ? "Guardando..." : "Guardar"}
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-gray-900">Posts del Blog</h2>
        <div className="flex gap-2">
          <button
            onClick={fetchPosts}
            className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50"
          >
            <RefreshCw className="w-4 h-4" />
            Actualizar
          </button>
          <button
            onClick={() => setShowEditor(true)}
            className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
          >
            <Plus className="w-4 h-4" />
            Nuevo Post
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
        </div>
      ) : posts.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl">
          <Image className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No hay posts todavía</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Post</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Categoría</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {posts.map((post) => (
                <tr key={post.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-12 h-12 rounded-lg object-cover"
                        onError={(e) => e.target.src = 'https://via.placeholder.com/48'}
                      />
                      <div>
                        <div className="font-medium text-gray-900 line-clamp-1">{post.title}</div>
                        <div className="text-sm text-gray-500">{post.author}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="px-2 py-1 text-xs bg-gray-100 rounded-full">{post.category}</span>
                  </td>
                  <td className="px-6 py-4">
                    {post.scheduled_at && !post.published ? (
                      <span className="flex items-center gap-1 text-xs text-blue-600">
                        <Calendar className="w-3 h-3" />
                        Programado
                      </span>
                    ) : (
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        post.published ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"
                      }`}>
                        {post.published ? "Publicado" : "Borrador"}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {new Date(post.created_at).toLocaleDateString('es-MX')}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => handleTogglePublish(post)}
                        className="p-2 hover:bg-gray-100 rounded-lg"
                        title={post.published ? "Despublicar" : "Publicar"}
                      >
                        {post.published ? <EyeOff className="w-4 h-4 text-gray-500" /> : <Eye className="w-4 h-4 text-green-500" />}
                      </button>
                      <button
                        onClick={() => handleEditPost(post)}
                        className="p-2 hover:bg-gray-100 rounded-lg"
                        title="Editar"
                      >
                        <Edit className="w-4 h-4 text-blue-500" />
                      </button>
                      <button
                        onClick={() => handleDeletePost(post.id)}
                        className="p-2 hover:bg-gray-100 rounded-lg"
                        title="Eliminar"
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PostsTab;
